import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type {
  Employee,
  EmployeeRole,
  PaginatedEmployees,
  SalaryPayment,
} from '@/types/employee'
import type { EmployeeFormData, SalaryPaymentFormData } from '@/validators/employee'

export interface ListEmployeeParams {
  search?: string
  role?: EmployeeRole
  active?: boolean
  page?: number
  perPage?: number
}

const KEYS = {
  list: (params: ListEmployeeParams) => ['employees', params] as const,
  detail: (id: string) => ['employees', id] as const,
}

export function useEmployees(params: ListEmployeeParams = {}) {
  return useQuery({
    queryKey: KEYS.list(params),
    queryFn: () => api.get<PaginatedEmployees>('/employees', { params }).then((r) => r.data),
  })
}

export function useEmployee(id: string) {
  return useQuery({
    queryKey: KEYS.detail(id),
    queryFn: () => api.get<Employee>(`/employees/${id}`).then((r) => r.data),
    enabled: !!id,
  })
}

export function useCreateEmployee() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: EmployeeFormData) =>
      api.post<Employee>('/employees', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employees'] }),
  })
}

export function useUpdateEmployee(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: EmployeeFormData) =>
      api.put<Employee>(`/employees/${id}`, data).then((r) => r.data),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ['employees'] })
      qc.setQueryData(KEYS.detail(id), updated)
    },
  })
}

export function useTerminateEmployee() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, terminationDate }: { id: string; terminationDate?: string }) =>
      api
        .patch<Employee>(`/employees/${id}/terminate`, { terminationDate })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employees'] }),
  })
}

export function useActivateEmployee() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.patch<Employee>(`/employees/${id}/activate`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employees'] }),
  })
}

const SALARY_KEYS = {
  list: (employeeId: string) => ['employees', employeeId, 'salary-payments'] as const,
}

export function useSalaryPayments(employeeId: string) {
  return useQuery({
    queryKey: SALARY_KEYS.list(employeeId),
    queryFn: () =>
      api.get<SalaryPayment[]>(`/employees/${employeeId}/salary-payments`).then((r) => r.data),
    enabled: !!employeeId,
  })
}

export function useCreateSalaryPayment(employeeId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: SalaryPaymentFormData) =>
      api
        .post<SalaryPayment>(`/employees/${employeeId}/salary-payments`, data)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SALARY_KEYS.list(employeeId) })
      // Pagamento gera lançamento no fluxo de caixa
      qc.invalidateQueries({ queryKey: ['cash-transactions'] })
      qc.invalidateQueries({ queryKey: ['cash-accounts'] })
      qc.invalidateQueries({ queryKey: ['cash-flow-summary'] })
    },
  })
}
